<template>
  <div class="highlight-tuning">
    <h3>精彩分析 · 品类调试</h3>
    <p class="form-hint section-hint">
      各直播类型可单独调权重与智能时长。权重会自动归一化。
      智能时长：按峰值向两侧扩展整段高燃，邻近峰会合并，避免一场高燃被切成多片。
      词表同时用于弹幕命中与峰段 ASR 加权（逗号分隔）；留空则用内置默认词。
      峰段 ASR 总开关在「系统配置」；开启后对游戏/唱歌/舞蹈/其他均生效。
    </p>
    <el-form v-loading="loading" label-width="140px" style="max-width: 720px">
      <div
        v-for="item in categoryOptions"
        :key="item.key"
        class="weight-block"
      >
        <h5>{{ item.label }}（{{ item.key }}）</h5>
        <el-form-item label="音频权重">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].audio"
            :min="0"
            :max="1"
            :step="0.05"
            :precision="2"
          />
        </el-form-item>
        <el-form-item label="切镜权重">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].scene"
            :min="0"
            :max="1"
            :step="0.05"
            :precision="2"
          />
        </el-form-item>
        <el-form-item label="弹幕权重">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].danmaku"
            :min="0"
            :max="1"
            :step="0.05"
            :precision="2"
          />
        </el-form-item>
        <el-form-item label="智能最短">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].minSmartSeconds"
            :min="8"
            :max="120"
            :step="5"
          />
          <span class="unit-suffix">秒</span>
        </el-form-item>
        <el-form-item label="智能最长">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].maxSmartSeconds"
            :min="20"
            :max="300"
            :step="10"
          />
          <span class="unit-suffix">秒（游戏≈3 分钟；其他≈1.5 分钟；唱歌/舞蹈可更长）</span>
        </el-form-item>
        <el-form-item label="峰合并间隔">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].mergeGapSeconds"
            :min="0"
            :max="90"
            :step="2"
          />
          <span class="unit-suffix">秒内邻近峰合并为同一高燃</span>
        </el-form-item>
        <el-form-item label="峰后最少">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].minTailSeconds"
            :min="4"
            :max="60"
            :step="1"
          />
          <span class="unit-suffix">秒（减少话尾截断）</span>
        </el-form-item>
        <el-form-item label="峰前比例">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].leadRatio"
            :min="0.15"
            :max="0.5"
            :step="0.05"
            :precision="2"
          />
          <span class="unit-suffix">填充时峰前占比（默认约 0.30）</span>
        </el-form-item>
        <el-form-item label="回落容忍">
          <el-input-number
            v-model="form.highlightCategoryWeights[item.key].dipToleranceSeconds"
            :min="0"
            :max="25"
            :step="1"
          />
          <span class="unit-suffix">秒（短暂掉音仍继续扩段；唱歌宜偏大）</span>
        </el-form-item>
        <el-form-item label="弹幕关键词">
          <el-input
            v-model="keywordText[item.key]"
            type="textarea"
            :rows="2"
            placeholder="666,卧槽,绝了"
          />
        </el-form-item>
      </div>
      <el-form-item>
        <el-button type="primary" @click="save">保存</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { fetchAdminConfig, updateAdminConfig } from '../api/admin';

const DEFAULT_WEIGHTS = {
  GAME: {
    audio: 0.72,
    scene: 0.18,
    danmaku: 0.10,
    minSmartSeconds: 25,
    maxSmartSeconds: 180,
    mergeGapSeconds: 12,
    minTailSeconds: 12,
    leadRatio: 0.30,
    dipToleranceSeconds: 8,
    keywords: ['666', '卧槽', '绝了', '牛', '杀疯了', '团灭', '翻盘'],
  },
  SHOW: {
    audio: 0.38,
    scene: 0.15,
    danmaku: 0.47,
    minSmartSeconds: 15,
    maxSmartSeconds: 90,
    mergeGapSeconds: 10,
    minTailSeconds: 8,
    leadRatio: 0.35,
    dipToleranceSeconds: 10,
    keywords: ['好看', '漂亮', '哈哈', '笑死', '宠粉', '冲', '大气', '爱了', '绝绝子', '美哭了', '心动', '礼物', '宝', 'YYDS', '聊', '哈哈哈'],
  },
  SING: {
    audio: 0.82,
    scene: 0.05,
    danmaku: 0.13,
    minSmartSeconds: 100,
    maxSmartSeconds: 300,
    mergeGapSeconds: 50,
    minTailSeconds: 60,
    leadRatio: 0.35,
    dipToleranceSeconds: 16,
    keywords: ['好听', '绝了', '封神', '泪目', '安可', '高音', '鸡皮疙瘩', '破音', '神仙嗓', '转音', '副歌', '哭了', '太顶了', 'yyds'],
  },
  DANCE: {
    audio: 0.82,
    scene: 0.05,
    danmaku: 0.13,
    minSmartSeconds: 100,
    maxSmartSeconds: 300,
    mergeGapSeconds: 50,
    minTailSeconds: 60,
    leadRatio: 0.35,
    dipToleranceSeconds: 16,
    keywords: ['好看', '绝了', '封神', '太顶了', '卡点', '节奏', '跳舞', '美哭了', 'yyds', '爱了'],
  },
};

function cloneWeights(source) {
  const out = {};
  Object.keys(DEFAULT_WEIGHTS).forEach((key) => {
    const src = (source && source[key]) || DEFAULT_WEIGHTS[key];
    const fallback = DEFAULT_WEIGHTS[key];
    out[key] = {
      audio: src.audio != null ? src.audio : fallback.audio,
      scene: src.scene != null ? src.scene : fallback.scene,
      danmaku: src.danmaku != null ? src.danmaku : fallback.danmaku,
      minSmartSeconds: src.minSmartSeconds != null ? src.minSmartSeconds : fallback.minSmartSeconds,
      maxSmartSeconds: src.maxSmartSeconds != null ? src.maxSmartSeconds : fallback.maxSmartSeconds,
      mergeGapSeconds: src.mergeGapSeconds != null ? src.mergeGapSeconds : fallback.mergeGapSeconds,
      minTailSeconds: src.minTailSeconds != null ? src.minTailSeconds : fallback.minTailSeconds,
      leadRatio: src.leadRatio != null ? src.leadRatio : fallback.leadRatio,
      dipToleranceSeconds: src.dipToleranceSeconds != null ? src.dipToleranceSeconds : fallback.dipToleranceSeconds,
      keywords: [...(src.keywords || fallback.keywords)],
    };
  });
  return out;
}

export default {
  name: 'HighlightTuning',
  data() {
    return {
      loading: false,
      categoryOptions: [
        { key: 'GAME', label: '游戏' },
        { key: 'SING', label: '唱歌' },
        { key: 'DANCE', label: '舞蹈' },
        { key: 'SHOW', label: '其他' },
      ],
      keywordText: {
        GAME: '',
        SHOW: '',
        SING: '',
        DANCE: '',
      },
      form: {
        highlightCategoryWeights: cloneWeights(),
      },
      /** 保存时合并进完整配置，避免覆盖其它开关 */
      fullConfig: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    syncKeywordTextFromForm() {
      Object.keys(this.form.highlightCategoryWeights).forEach((key) => {
        const keywords = this.form.highlightCategoryWeights[key].keywords || [];
        this.keywordText[key] = keywords.join(',');
      });
    },
    applyKeywordTextToForm() {
      Object.keys(this.form.highlightCategoryWeights).forEach((key) => {
        const raw = this.keywordText[key] || '';
        this.form.highlightCategoryWeights[key].keywords = raw
          .split(/[,，]/)
          .map(item => item.trim())
          .filter(Boolean);
      });
    },
    async load() {
      this.loading = true;
      try {
        const data = await fetchAdminConfig();
        this.fullConfig = data;
        this.form.highlightCategoryWeights = cloneWeights(data.highlightCategoryWeights);
        this.syncKeywordTextFromForm();
      } catch (e) {
        this.$message.error(e.message || '加载配置失败');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      try {
        this.applyKeywordTextToForm();
        const payload = {
          ...(this.fullConfig || {}),
          highlightCategoryWeights: this.form.highlightCategoryWeights,
        };
        await updateAdminConfig(payload);
        this.$message.success('品类调试配置已保存');
        await this.load();
      } catch (e) {
        this.$message.error(e.message || '保存失败');
      }
    },
  },
};
</script>

<style scoped>
.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.section-hint {
  margin: 0 0 16px;
}
.unit-suffix {
  margin-left: 8px;
  color: #6b7280;
  font-size: 13px;
}
.weight-block {
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}
.weight-block h5 {
  margin: 0 0 10px;
  font-size: 14px;
  color: #111827;
}
</style>
